import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEffect } from 'react';

export default function TeslaShippingCostPage() {
  // Dynamically manage SEO browser titles & meta descriptions
  useEffect(() => {
  document.title = "Tesla Shipping Cost USA | Price Guide & Instant Quote";

  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }

  metaDescription.setAttribute(
    'content',
    'Discover Tesla shipping costs in the USA for 2026. Compare open and enclosed transport pricing, calculate cost per mile, and get an instant quote for cross-country EV shipping.'
  );

  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }

  metaKeywords.setAttribute(
    'content',
    'Tesla shipping cost USA 2026, Tesla shipping cost calculator, Cost to ship Tesla cross-country, Tesla transport pricing USA, EV transport pricing trends 2026, Tesla shipping cost per mile in the USA, Cheapest way to ship Tesla, Tesla car shipping rates, Cross-country Tesla shipping cost, Open vs enclosed Tesla shipping cost, AI cost prediction for car shipping, Vehicle logistics pricing analysis, Electric vehicle shipping cost, Tesla delivery pricing guide'
  );
}, []);

  const costData = [
    { distance: "~500 miles", open: "~$775", enclosed: "~$1,190" },
    { distance: "~1,000 miles", open: "~$1,175", enclosed: "~$1,840" },
    { distance: "~1,500 miles", open: "~$1,575", enclosed: "~$2,490" },
    { distance: "~2,000 miles", open: "~$1,825", enclosed: "~$2,940" },
    { distance: "~2,800 miles (coast-to-coast)", open: "~$2,225", enclosed: "~$3,660" }
  ];

  const faqs = [
    {
      question: "How much does it cost to ship a Tesla cross-country?",
      answer: "A coast-to-coast move (about 2,800 miles) runs roughly $2,225 open or $3,660 enclosed at our published rates. Get an exact quote for your route in 60 seconds."
    },
    {
      question: "Is enclosed transport worth the extra cost?",
      answer: "For new, high-value, wrapped, or Performance Teslas -- or harsh-weather routes -- the added protection is usually worth it. For standard moves, open transport is a smart, lower-cost choice."
    },
    {
      question: "Are there any hidden fees?",
      answer: "No. Your quote is the price. The only change would be if details you provided (like operability or location access) turn out different at pickup."
    }
  ];

  // Combined Graph Schema (Product Pricing + Dynamic FAQPage)
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        "name": "Tesla Shipping Cost",
        "description": "Cost to ship Tesla across USA",
        "offers": {
          "@type": "Offer",
          "priceCurrency": "USD",
          "price": "775-3660",
          "availability": "https://schema.org/InStock"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Injecting Structured SEO Data into the DOM */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />

      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          
          {/* Header */}
          <div className="text-center mb-12">
            <p className="overline mb-4 text-accent uppercase tracking-wider font-semibold">Pricing &amp; Rates</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-foreground" data-testid="cost-title">
              Tesla Shipping Cost: 2026 Price Guide
            </h1>
          </div>

          <div className="glass border-l-4 border-l-accent border-y border-r border-border rounded-r-xl p-6 mb-8 text-lg text-foreground">
            Shipping a Tesla generally begins at approximately $1.05 per mile with a $250 base fee for open transport (or about $1.68 per mile + $350 for enclosed service). As the shipping distance increases, the per-mile rate becomes more economical — dropping to as low as $0.50 per mile on longer routes. In practical terms, this translates to around $775 for a short open shipment and up to roughly $2,225 for a cross-country open move. For a precise, route-specific estimate, request an instant quote in under 60 seconds.
          </div>

          <div className="text-center mb-12">
            <Link to="/quote">
              <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold shadow-lg shadow-accent/20 hover:brightness-110 transition-all">
                Get Instant Quote
              </Button>
            </Link>
          </div>

          {/* Main Content Body */}
          <div className="space-y-8 text-muted-foreground md:text-lg leading-relaxed">
            
            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
                Tesla shipping cost by distance
              </h2>
              <p className="mb-6 text-sm md:text-base">
                Figures are illustrative examples based on our published per-mile rates. The quote tool gives your exact price.
              </p>

              <div className="overflow-x-auto border border-border rounded-xl glass my-6">
                <table className="w-full text-left border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-foreground">
                      <th className="p-4 font-semibold">Distance</th>
                      <th className="p-4 font-semibold">Open</th>
                      <th className="p-4 font-semibold">Enclosed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {costData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/10 transition-colors">
                        <td className="p-4 font-medium text-foreground">{row.distance}</td>
                        <td className="p-4 text-muted-foreground">{row.open}</td>
                        <td className="p-4 text-muted-foreground">{row.enclosed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-center mt-6">
                <Link to="/quote">
                  <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black">
                    Get Instant Quote
                  </Button>
                </Link>
              </div>
            </div>

            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
                What Determines Your Tesla Shipping Quote?  
              </h2>
              <ul className="list-disc pl-6 space-y-3">
                <li><strong className="text-foreground">Distance.</strong> The primary cost driver--pricing is calculated per mile, along with a base fee.</li>
                <li><strong className="text-foreground">Open vs. Enclosed.</strong> Enclosed shipping typically costs 30--60% more, reflecting the added level of protection.
</li>
                <li><strong className="text-foreground">Market Availability.</strong>  High-volume corridors (such as CA--TX) benefit from greater carrier availability and more competitive pricing, while remote locations can increase costs.
</li>
                <li><strong className="text-foreground">Seasonality.</strong> Peak periods, particularly snowbird season (fall and spring), tighten capacity on north--south routes and often push rates higher</li>
                <li><strong className="text-foreground">Vehicle condition.</strong>  Inoperable Teslas require specialized equipment, such as a winch, which adds to the overall cost.
</li>
                <li><strong className="text-foreground">Delivery method.</strong> Door-to-door transport is the standard and most convenient option, while terminal-to-terminal service may offer modest savings.
</li>
              </ul>
            </div>

            <div>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-3">
                Why our pricing is transparent

              </h2>
              <p>
                Some brokers promote artificially low quotes to win your business, only to face challenges securing a carrier at that price--often leading to last-minute price increases. ShipTesla takes a different approach with margin-protected dispatch, ensuring the quote you receive reflects real market conditions and can be honored by a qualified carrier. Additionally, your deposit remains fully refundable until dispatch is officially confirmed, so you're never committed to a rate that isn't deliverable.

              </p>
            </div>

          </div>

          {/* Page-Specific FAQs */}
          <div className="mt-16 border-t border-border pt-12">
            <h2 className="font-outfit text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-6 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="glass border border-border rounded-xl px-6 data-[state=open]:border-accent/50"
                >
                  <AccordionTrigger className="text-left font-medium hover:no-underline py-5 text-foreground">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* CTA Block */}
          <div className="mt-16 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button className="bg-accent text-accent-foreground rounded-full px-8 dark:hover:text-black font-semibold">
                  Get Instant Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Related Guides Links Layout */}
          <div className="mt-16 pt-8 border-t border-border">
            <h3 className="font-outfit text-lg font-semibold text-foreground mb-4">Related guides</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ship-a-tesla" className="text-accent hover:underline">How to Ship a Tesla</Link></li>
              <li><Link to="/enclosed-tesla-transport" className="text-accent hover:underline">Enclosed Tesla Transport</Link></li>
              <li><Link to="/routes/california-to-florida" className="text-accent hover:underline">Ship a Tesla: California to Florida</Link></li>
            </ul>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}



